import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardList, Info, Lock, Mail, Search, Send, X } from 'lucide-react';
import { ToolsSection } from '@/components/journal/ToolsColumn';
import { QuestionnairePicker } from '@/components/questionnaires/QuestionnairePicker';
import {
  filterPickerItems,
  QuestionnairePickerRow,
} from '@/components/questionnaires/QuestionnairePickerCatalog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { useQuestionnaireFavorites } from '@/hooks/useQuestionnaireLibraryPrefs';
import type { FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  toQuestionnairePickerItems,
  type QuestionnairePickerItem,
} from '@/lib/questionnairePickerLabel';
import type { JournalQuestionnaireAllowSource } from '@/lib/journalAppointmentQuestionnaires';
import {
  canUndoStepQuestionnaireSend,
  courseQuestionnairePrimaryScore,
  joinStepSendNames,
  previewStepSendItems,
  questionnaireIsOnStep,
  sendableStepTemplates,
  stepSendAlreadyName,
  templatesInAllowOrder,
} from '@/lib/stepSendQuestionnaires';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { courseQuestionnaireName } from '@/lib/courseUiHelpers';
import { indexQuestionnaireTemplates } from '@/lib/questionnaireDisplayName';
import { groupQuestionnairesByTag } from '@/lib/questionnaireTags';
import type { StaffCourseQuestionnaire } from '@/lib/treatmentCourseStaffView';
import { cn } from '@/utils/cn';

function TaggedQuestionnaireList({
  items,
  restricted,
  onStepIds,
  picked,
  favoriteIds,
  untaggedLabel,
  onToggle,
  onToggleFavorite,
}: {
  items: readonly QuestionnairePickerItem[];
  restricted: boolean;
  onStepIds: ReadonlySet<string>;
  picked: readonly string[];
  favoriteIds: readonly string[];
  untaggedLabel: string;
  onToggle: (id: string) => void;
  onToggleFavorite: (id: string) => void;
}) {
  const sections = groupQuestionnairesByTag(items);
  const showTagHeaders = sections.some((section) => section.tag != null);
  return (
    <div className="qp-tags" data-testid="step-questionnaire-tags">
      {sections.map((section) => {
        const label = section.tag ?? (showTagHeaders ? untaggedLabel : null);
        return (
          <section
            key={section.tag ?? '\u0000'}
            className="qp-tag"
            data-testid="step-questionnaire-tag"
            data-tag={section.tag ?? ''}
            aria-label={label ?? undefined}
          >
            {label ? <p className="qp-tag-h">{label}</p> : null}
            <ul className="pk-l" role="listbox" aria-multiselectable="true">
              {section.items.map((item) => {
                const onThisStep = restricted && onStepIds.has(item.id);
                return (
                  <QuestionnairePickerRow
                    key={`${section.tag ?? ''}:${item.id}`}
                    item={item}
                    compact
                    selected={onThisStep || picked.includes(item.id)}
                    locked={onThisStep}
                    favorite={favoriteIds.includes(item.id)}
                    onToggle={() => onToggle(item.id)}
                    onToggleFavorite={() => onToggleFavorite(item.id)}
                  />
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export type StepSendConfirmation = {
  templateIds: string[];
  names: string[];
  formsAssignmentIds: string[];
  assignmentIds: string[];
};

export function StepSendQuestionnairePanel({
  templates,
  allowedIds,
  questionnaires,
  picked,
  onPickedChange,
  patientName,
  canSend,
  sending,
  confirmation,
  onSend,
  onUndo,
  undoPending,
  language,
  onViewResponse,
  sendButtonVariant = 'primary',
  hideHeading = false,
  allowSource = 'step',
  allowListPending = false,
  allowListError = false,
  variant = 'box',
}: {
  templates: FormsTemplateSummary[];
  allowedIds: readonly string[];
  questionnaires: StaffCourseQuestionnaire[];
  picked: string[];
  onPickedChange: (ids: string[]) => void;
  patientName: string;
  canSend: boolean;
  sending: boolean;
  confirmation: StepSendConfirmation | null;
  onSend: (ids: string[]) => void;
  onUndo: () => void;
  undoPending: boolean;
  language: string;
  onViewResponse?: (formsAssignmentId: string) => void;
  sendButtonVariant?: 'primary' | 'secondary';
  hideHeading?: boolean;
  /** Where `allowedIds` came from. Same labels as the journal step tools. */
  allowSource?: JournalQuestionnaireAllowSource;
  /** True while the step's Tilladte are still being resolved. */
  allowListPending?: boolean;
  /** True when the live template step could not be read. */
  allowListError?: boolean;
  /** `tools` folds send and responses into Værktøjer sections. `box` is the single card. */
  variant?: 'box' | 'tools';
}) {
  const body = (
    <StepSendQuestionnaireBody
      compact={false}
      variant={variant}
      sendButtonVariant={sendButtonVariant}
      hideHeading={hideHeading}
      templates={templates}
      allowedIds={allowedIds}
      questionnaires={questionnaires}
      picked={picked}
      onPickedChange={onPickedChange}
      patientName={patientName}
      canSend={canSend}
      sending={sending}
      confirmation={confirmation}
      onSend={onSend}
      onUndo={onUndo}
      undoPending={undoPending}
      language={language}
      onViewResponse={onViewResponse}
      allowSource={allowSource}
      allowListPending={allowListPending}
      allowListError={allowListError}
    />
  );
  if (variant === 'tools') {
    return (
      <div className="sk fl-tools-q" data-testid="step-send-questionnaires">
        {body}
      </div>
    );
  }
  return (
    <section className="box spp sk" data-testid="step-send-questionnaires">
      {body}
    </section>
  );
}

export function StepSendQuestionnaireQuickPick({
  open,
  onClose,
  templates,
  allowedIds,
  questionnaires,
  picked,
  onPickedChange,
  patientName,
  canSend,
  sending,
  confirmation,
  onSend,
  onUndo,
  undoPending,
  language,
  onViewResponse,
  allowSource = 'step',
  allowListPending = false,
  allowListError = false,
}: {
  open: boolean;
  onClose: () => void;
  templates: FormsTemplateSummary[];
  allowedIds: readonly string[];
  questionnaires: StaffCourseQuestionnaire[];
  picked: string[];
  onPickedChange: (ids: string[]) => void;
  patientName: string;
  canSend: boolean;
  sending: boolean;
  confirmation: StepSendConfirmation | null;
  onSend: (ids: string[]) => void;
  onUndo: () => void;
  undoPending: boolean;
  language: string;
  onViewResponse?: (formsAssignmentId: string) => void;
  allowSource?: JournalQuestionnaireAllowSource;
  allowListPending?: boolean;
  allowListError?: boolean;
}) {
  const { t } = useTranslation();
  const desktop = useIsDesktopNav();
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, open]);

  if (!open) return null;

  const body = (
    <StepSendQuestionnaireBody
      compact
      title={t('treatment_courses.step_send_title')}
      onClose={onClose}
      templates={templates}
      allowedIds={allowedIds}
      questionnaires={questionnaires}
      picked={picked}
      onPickedChange={onPickedChange}
      patientName={patientName}
      canSend={canSend}
      sending={sending}
      confirmation={confirmation}
      onSend={onSend}
      onUndo={onUndo}
      undoPending={undoPending}
      language={language}
      onViewResponse={onViewResponse}
      allowSource={allowSource}
      allowListPending={allowListPending}
      allowListError={allowListError}
    />
  );

  if (!desktop) {
    return (
      <div
        ref={wrapRef}
        className="sk-sheet spsheet sk"
        role="dialog"
        aria-label={t('treatment_courses.step_send_title')}
        data-testid="step-send-sheet"
      >
        <div className="handle" aria-hidden />
        {body}
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="qpick dsk spop sk"
      role="dialog"
      aria-label={t('treatment_courses.step_send_title')}
      data-testid="step-send-popover"
    >
      {body}
    </div>
  );
}

function StepSendQuestionnaireBody({
  compact,
  title,
  onClose,
  templates,
  allowedIds,
  questionnaires,
  picked,
  onPickedChange,
  patientName,
  canSend,
  sending,
  confirmation,
  onSend,
  onUndo,
  undoPending,
  language,
  onViewResponse,
  sendButtonVariant = 'primary',
  hideHeading = false,
  allowSource = 'step',
  allowListPending = false,
  allowListError = false,
  variant = 'box',
}: {
  compact: boolean;
  title?: string;
  onClose?: () => void;
  templates: FormsTemplateSummary[];
  allowedIds: readonly string[];
  questionnaires: StaffCourseQuestionnaire[];
  picked: string[];
  onPickedChange: (ids: string[]) => void;
  patientName: string;
  canSend: boolean;
  sending: boolean;
  confirmation: StepSendConfirmation | null;
  onSend: (ids: string[]) => void;
  sendButtonVariant?: 'primary' | 'secondary';
  hideHeading?: boolean;
  onUndo: () => void;
  undoPending: boolean;
  language: string;
  onViewResponse?: (formsAssignmentId: string) => void;
  allowSource?: JournalQuestionnaireAllowSource;
  allowListPending?: boolean;
  allowListError?: boolean;
  variant?: 'box' | 'tools';
}) {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const favorites = useQuestionnaireFavorites(activeClinicId);
  const [query, setQuery] = useState('');
  const [allOpen, setAllOpen] = useState(false);
  const templatesById = useMemo(() => indexQuestionnaireTemplates(templates), [templates]);
  const restricted = allowedIds.length > 0;
  const onStep = questionnaires.filter(questionnaireIsOnStep);
  const onStepIds = new Set(onStep.map((row) => row.forms_template_id));
  // Tilladte stay in the list after they have been sent. The free-choice
  // catalogue still hides forms that are already on the step.
  const configured = templatesInAllowOrder(templates, allowedIds);
  const sendable = sendableStepTemplates(templates, allowedIds, onStepIds);
  const catalogTemplates = restricted ? configured : sendable;
  const pool = restricted ? configured : templates;
  const items = useMemo(
    () =>
      toQuestionnairePickerItems(
        catalogTemplates,
        i18n.language,
        t('treatment_courses.q_unnamed'),
        (count) => t('forms.picker.questions', { count }),
      ),
    [catalogTemplates, i18n.language, t],
  );
  const pickerItems = useMemo(
    () =>
      toQuestionnairePickerItems(
        pool,
        i18n.language,
        t('treatment_courses.q_unnamed'),
        (count) => t('forms.picker.questions', { count }),
      ),
    [i18n.language, pool, t],
  );
  const lockedIds = pool.filter((row) => onStepIds.has(row.id)).map((row) => row.id);
  const favoriteIds = favorites.data ?? [];
  const filtered = useMemo(() => filterPickerItems(items, query, []), [items, query]);
  const visible = query.trim()
    ? filtered
    : restricted
      ? items
      : previewStepSendItems(items, favoriteIds, picked);
  const allowLine = allowListPending ? null : (
    <p className={restricted ? 'sp-lock' : 'sp-free'} data-testid="step-allow-list-status">
      {restricted ? <Lock className="i xs" aria-hidden /> : <Info className="i xs" aria-hidden />}
      {restricted
        ? allowSource === 'general'
          ? t('journal.questionnaires_from_general')
          : t('journal.questionnaires_from_step')
        : allowListError
          ? t('journal.questionnaires_step_error')
          : t('treatment_courses.step_send_free', { count: templates.length })}
    </p>
  );

  const undoable = Boolean(
    confirmation &&
      confirmation.formsAssignmentIds.some((id) => {
        const row = questionnaires.find((item) => item.forms_assignment_id === id);
        return !row || canUndoStepQuestionnaireSend(row.assignment_status);
      }),
  );

  function togglePick(id: string) {
    onPickedChange(picked.includes(id) ? picked.filter((value) => value !== id) : [...picked, id]);
  }

  const catalog = (
    <div className="sp-pick">
      <div className="pk-search sp-s">
        <div className="search">
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('treatment_courses.picker_search')}
            aria-label={t('treatment_courses.picker_search')}
            leadingIcon={<Search className="i sm" aria-hidden />}
          />
        </div>
      </div>
      {query.trim() ? (
        <p className="sp-n">
          {t(
            filtered.length === 1
              ? 'treatment_courses.step_send_search_one'
              : 'treatment_courses.step_send_search_other',
            { count: filtered.length, query: query.trim() },
          )}
        </p>
      ) : null}
      {visible.length > 0 ? (
        <TaggedQuestionnaireList
          items={visible}
          restricted={restricted}
          onStepIds={onStepIds}
          picked={picked}
          favoriteIds={favoriteIds}
          untaggedLabel={t('forms.no_tags', 'Ingen tags')}
          onToggle={(id) => {
            if (restricted && onStepIds.has(id)) return;
            togglePick(id);
          }}
          onToggleFavorite={(id) => favorites.toggle.mutate(id)}
        />
      ) : null}
      <p className="sp-all">
        <button type="button" className="lnk" onClick={() => setAllOpen(true)}>
          {t('treatment_courses.step_send_show_all', 'Vis alle skemaer')}
        </button>
      </p>
    </div>
  );

  const allPicker = (
    <QuestionnairePicker
      open={allOpen}
      title={t('forms.modal.title')}
      items={pickerItems}
      lockedIds={lockedIds}
      clinicId={activeClinicId}
      mode="select"
      showLists
      showFavorites
      initialSelectedIds={picked}
      onClose={() => setAllOpen(false)}
      onConfirm={(payload) => {
        const locked = new Set(lockedIds);
        onPickedChange(payload.ids.filter((id) => !locked.has(id)));
        setAllOpen(false);
      }}
    />
  );

  const footer = (
    <div className="sp-f">
      <p className="via">
        <Mail className="i xs" aria-hidden />
        {t('treatment_courses.step_send_via_portal_email')}
      </p>
      <Button
        type="button"
        variant={sendButtonVariant}
        className="sp-send"
        disabled={!canSend || picked.length === 0 || sending}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onSend(picked)}
      >
        <Send className="i sm" aria-hidden />
        {picked.length > 0
          ? t('treatment_courses.step_send_button_count', { count: picked.length })
          : t('treatment_courses.step_send_button')}
      </Button>
    </div>
  );

  const confirmationBlock =
    confirmation && confirmation.names.length > 0 ? (
      <div className="sp-ok" data-testid="step-send-confirmation">
        <span className="ic" aria-hidden>
          <Check className="i xs" />
        </span>
        <div className="grow">
          <b>
            {t('treatment_courses.step_send_ok_title', {
              name: confirmation.names.join(', '),
              patient: patientName,
            })}
          </b>
          <span>{t('treatment_courses.step_send_ok_via')}</span>
        </div>
        {undoable ? (
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            className="btn"
            disabled={undoPending}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onUndo}
          >
            {t('treatment_courses.step_send_undo')}
          </Button>
        ) : null}
      </div>
    ) : null;

  const sentList =
    onStep.length > 0 ? (
      <ul className="sent">
        {onStep.map((row) => {
          const name = courseQuestionnaireName(
            row,
            language,
            t('treatment_courses.q_unnamed'),
            templatesById,
          );
          const score = courseQuestionnairePrimaryScore(row.score);
          const justSent = confirmation?.templateIds.includes(row.forms_template_id);
          const when = row.completed_at ?? row.sent_at;
          return (
            <li
              key={row.assignment_id ?? row.forms_assignment_id ?? row.forms_template_id}
              className={cn('srw', justSent && 'new')}
            >
              <div className="sr-t">
                <span className="nm">{name}</span>
                <span
                  className={cn(
                    'chip',
                    row.completed_at ? 'chip-green' : justSent ? 'chip-teal' : 'chip-neutral',
                  )}
                >
                  {row.completed_at && score != null
                    ? t('treatment_courses.step_send_completed', { score })
                    : row.completed_at
                      ? t('treatment_courses.q_state_completed')
                      : t('treatment_courses.q_state_sent')}
                </span>
              </div>
              <div className="sr-m num">
                {row.completed_at
                  ? t('treatment_courses.step_send_answered_at', {
                      when: when ? formatCourseDateTime(when, language) : '',
                    })
                  : justSent
                    ? t('treatment_courses.step_send_sent_via', {
                        when: when ? formatCourseDateTime(when, language) : '',
                      })
                    : when
                      ? t('treatment_courses.step_send_sent_unanswered', {
                          when: formatCourseDateTime(when, language),
                        })
                      : t('treatment_courses.q_state_sent')}
                {row.completed_at && row.forms_assignment_id && onViewResponse ? (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="link"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onViewResponse(row.forms_assignment_id!)}
                    >
                      {t('treatment_courses.workspace_open_answers')}
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    ) : null;

  if (compact) {
    return (
      <>
        <div className="sp-h">
          <div className="grow">
            <h3>{title}</h3>
            {allowLine}
          </div>
          {onClose ? (
            <button type="button" className="ibtn" aria-label={t('common.close')} onClick={onClose}>
              <X className="i sm" />
            </button>
          ) : null}
        </div>
        {confirmationBlock}
        <div className="sh-sc">
          {allowListPending ? null : catalog}
          {onStep.length > 0 ? (
            <p className="sp-already">
              {t('treatment_courses.step_send_already', {
                names: joinStepSendNames(
                  onStep.map((row) =>
                    stepSendAlreadyName(
                      courseQuestionnaireName(
                        row,
                        language,
                        t('treatment_courses.q_unnamed'),
                        templatesById,
                      ),
                      Boolean(row.completed_at),
                      t('treatment_courses.step_send_filled'),
                    ),
                  ),
                  i18n.language,
                ),
              })}
            </p>
          ) : null}
        </div>
        {footer}
        {allPicker}
      </>
    );
  }

  if (variant === 'tools') {
    return (
      <>
        <ToolsSection
          title={t('treatment_courses.step_send_title')}
          icon={<ClipboardList className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
        >
          {allowLine}
          {confirmationBlock}
          {allowListPending ? null : catalog}
          {footer}
        </ToolsSection>
        <ToolsSection
          title={t('journal.recent_scores')}
          icon={<Check className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
          count={onStep.length}
        >
          {sentList ?? <p className="text-sm text-ds-muted">{t('journal.no_scores')}</p>}
        </ToolsSection>
        {allPicker}
      </>
    );
  }

  return (
    <>
      {hideHeading ? null : (
        <div className="sp-h">
          <h3>{t('treatment_courses.step_send_title')}</h3>
        </div>
      )}
      {allowLine}
      {confirmationBlock}
      {allowListPending ? null : catalog}
      {footer}
      {sentList ? (
        <>
          <p className="sp-sub">
            {t('treatment_courses.step_send_sent')}
            <span className="n num">{onStep.length}</span>
          </p>
          {sentList}
        </>
      ) : null}
      {allPicker}
    </>
  );
}
